
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Team {
  id: string;
  name: string;
  description?: string;
  members_count?: number;
}

interface ClientTeamsProps {
  clientId: string;
}

export const ClientTeams = ({ clientId }: ClientTeamsProps) => {
  const [assignedTeams, setAssignedTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClientTeams = async () => {
      setIsLoading(true);
      try {
        console.log("Fetching teams for client ID:", clientId);
        
        // Fetch teams assigned to this client using rpc function
        const { data: clientTeamsData, error: clientTeamsError } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', clientId);

        if (clientTeamsError) {
          console.error('Error fetching client teams:', clientTeamsError);
          throw clientTeamsError;
        }

        if (!clientTeamsData || clientTeamsData.length === 0) {
          console.log("No teams assigned to this client");
          setAssignedTeams([]);
          setIsLoading(false);
          return;
        }

        const teamIds = clientTeamsData.map(item => item.team_id);
        console.log("Client team IDs:", teamIds);
        
        // Fetch the team details
        const { data: teamsData, error: teamsError } = await supabase
          .from('teams')
          .select('id, name, description')
          .in('id', teamIds);
          
        if (teamsError) {
          console.error('Error fetching team details:', teamsError);
          throw teamsError;
        }

        // For each team, get the member count using get_team_members function
        const teamsWithCounts = await Promise.all(
          (teamsData || []).map(async (team) => {
            // Use our new get_team_members RPC function to get team members
            const { data: teamMembers, error: membersError } = await supabase
              .rpc('get_team_members', { team_id_param: team.id });
              
            if (membersError) {
              console.error(`Error getting members for team ${team.id}:`, membersError);
              return { ...team, members_count: 0 };
            }
            
            // Count only guard members by filtering those with role 'guard'
            const guardCount = teamMembers ? teamMembers.length : 0;
            return { ...team, members_count: guardCount };
          })
        );
        
        console.log("Teams with member counts:", teamsWithCounts);
        setAssignedTeams(teamsWithCounts);
      } catch (error) {
        console.error('Error fetching client teams:', error);
        setAssignedTeams([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (clientId) {
      fetchClientTeams();
    }
  }, [clientId]);

  if (isLoading) {
    return <div className="text-sm text-gray-500">Φόρτωση ομάδων...</div>;
  }

  if (assignedTeams.length === 0) {
    return <div className="text-sm text-gray-500">Δεν έχουν ανατεθεί ομάδες</div>;
  }

  return (
    <div className="mt-2">
      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
        {assignedTeams.map(team => (
          <div key={team.id} className="flex items-center">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 truncate">
              {team.name}
            </span>
            {team.members_count !== undefined && (
              <span className="ml-1 text-xs text-gray-500">
                {team.members_count} {team.members_count === 1 ? 'guard' : 'guards'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
